import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ImageOff } from "lucide-react";
import { cn } from "@/lib/utils";

type Attachment = {
  key: string;
  title: string;
  filename: string | null;
  url: string | null;
};

export default function CustomerAttachmentsTab({ customerId }: { customerId: string }) {
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await apiFetch<{ data: { customer_id: number; attachments: Attachment[] } }>(
          `/customer-profile/${customerId}/attachments`
        );

        if (!mounted) return;
        setAttachments(res?.data?.attachments ?? []);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? "Failed to load attachments");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [customerId]);

  const visible = useMemo(
    () => attachments.filter((a) => a.filename), // show only fields that have something stored
    [attachments]
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading attachments...
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-sm text-destructive">{error}</div>;
  }

  if (!visible.length) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        No attachments found for this customer.
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {visible.map((a) => (
          <Card key={a.key} className="shadow-card overflow-hidden">
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">
                <span className="truncate">{a.title}</span>
                <span className="text-xs text-muted-foreground truncate">{a.filename}</span>
              </CardTitle>
            </CardHeader>

            <CardContent className="p-3">
              <div
                className={cn(
                  "aspect-[3/4] w-full rounded-xl bg-muted overflow-hidden flex items-center justify-center border"
                )}
              >
                {a.url ? (
                  <a href={a.url} target="_blank" rel="noreferrer" className="block h-full w-full">
                    <img
                      src={a.url}
                      alt={a.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <div className="flex flex-col items-center justify-center text-muted-foreground gap-2">
                    <ImageOff className="h-8 w-8" />
                    <div className="text-xs">File not found / URL missing</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}